/**
 * Сохранение настроек вкладки «Слои» (главная карта) между перезагрузками страницы.
 */
import type { MvtFilters, MvtLayerSettings } from "@/contexts/MapUiContext";

const STORAGE_KEY = "geopulseml.mapUi.layers.v1";

/** Подложки из LayerPanel — только они переносятся в activeTool при выборе карты. */
export const PERSISTED_BASE_MAP_TOOLS = [
  "map-osm",
  "map-satellite",
  "map-topo",
  "map-dark",
  "map-blank",
  "map-martin",
] as const;

export const DEFAULT_MVT_FILTERS: MvtFilters = {
  objectTypes: [],
  statuses: [],
  organizations: [],
  geometryPrimitiveCodes: [],
};

export const DEFAULT_MVT_LAYER_SETTINGS: MvtLayerSettings = {
  showPoints: true,
  showLines: true,
  showPolygons: true,
  pointOpacity: 1,
  lineOpacity: 0.8,
  polygonOpacity: 0.35,
  symbolOnlyMode: false,
  calloutsEnabled: false,
  iconsLabelsMode: false,
};

export type MapLayersPersistedSnapshot = {
  activeTool: string | null;
  cadastralLayersVisible: boolean;
  cadastralOpacity: number;
  telegramIncidentsVisible: boolean;
  telegramOpacity: number;
  workItemsVisible: boolean;
  /** true — visibility брать из workItems; false/undefined — дефолт = feature flag (история: авто-запись `false` до #work-items layer). */
  workItemsUserTouched: boolean;
  workItemsOpacity: number;
  showWorkItemCreateButton: boolean;
  showBaseLayerSwitcher: boolean;
  showMapExportButton: boolean;
  mvtFilters: MvtFilters;
  mvtLayerSettings: MvtLayerSettings;
};

function isPersistableBasemap(tool: string | null): tool is string {
  return tool != null && (PERSISTED_BASE_MAP_TOOLS as readonly string[]).includes(tool);
}

export function readMapLayersPersisted(): Partial<MapLayersPersistedSnapshot> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const data = JSON.parse(raw) as Record<string, unknown>;
    if (!data || typeof data !== "object") return {};
    const out: Partial<MapLayersPersistedSnapshot> = {};

    if (typeof data.activeTool === "string" && isPersistableBasemap(data.activeTool)) {
      out.activeTool = data.activeTool;
    } else if (data.activeTool === null) {
      out.activeTool = null;
    }

    if (typeof data.cadastralLayersVisible === "boolean") out.cadastralLayersVisible = data.cadastralLayersVisible;
    if (typeof data.cadastralOpacity === "number" && Number.isFinite(data.cadastralOpacity)) {
      out.cadastralOpacity = clamp01(data.cadastralOpacity);
    }
    if (typeof data.telegramIncidentsVisible === "boolean") {
      out.telegramIncidentsVisible = data.telegramIncidentsVisible;
    }
    if (typeof data.telegramOpacity === "number" && Number.isFinite(data.telegramOpacity)) {
      out.telegramOpacity = clamp01(data.telegramOpacity);
    }
    if (typeof data.workItemsVisible === "boolean") out.workItemsVisible = data.workItemsVisible;
    if (data.workItemsUserTouched === true) out.workItemsUserTouched = true;
    if (typeof data.workItemsOpacity === "number" && Number.isFinite(data.workItemsOpacity)) {
      out.workItemsOpacity = clamp01(data.workItemsOpacity);
    }
    if (typeof data.showWorkItemCreateButton === "boolean") {
      out.showWorkItemCreateButton = data.showWorkItemCreateButton;
    }
    if (typeof data.showBaseLayerSwitcher === "boolean") out.showBaseLayerSwitcher = data.showBaseLayerSwitcher;
    if (typeof data.showMapExportButton === "boolean") out.showMapExportButton = data.showMapExportButton;

    if (data.mvtFilters && typeof data.mvtFilters === "object") {
      const f = data.mvtFilters as Record<string, unknown>;
      out.mvtFilters = {
        objectTypes: Array.isArray(f.objectTypes) ? f.objectTypes.map(Number).filter((n) => !Number.isNaN(n)) : [],
        statuses: Array.isArray(f.statuses) ? f.statuses.filter((s): s is string => typeof s === "string") : [],
        organizations: Array.isArray(f.organizations)
          ? f.organizations.map(Number).filter((n) => !Number.isNaN(n))
          : [],
        geometryPrimitiveCodes: Array.isArray(f.geometryPrimitiveCodes)
          ? f.geometryPrimitiveCodes.filter((c): c is string => typeof c === "string")
          : [],
      };
    }

    if (data.mvtLayerSettings && typeof data.mvtLayerSettings === "object") {
      const s = data.mvtLayerSettings as Record<string, unknown>;
      out.mvtLayerSettings = {
        showPoints: typeof s.showPoints === "boolean" ? s.showPoints : DEFAULT_MVT_LAYER_SETTINGS.showPoints,
        showLines: typeof s.showLines === "boolean" ? s.showLines : DEFAULT_MVT_LAYER_SETTINGS.showLines,
        showPolygons:
          typeof s.showPolygons === "boolean" ? s.showPolygons : DEFAULT_MVT_LAYER_SETTINGS.showPolygons,
        pointOpacity: readOpacity(s.pointOpacity, DEFAULT_MVT_LAYER_SETTINGS.pointOpacity),
        lineOpacity: readOpacity(s.lineOpacity, DEFAULT_MVT_LAYER_SETTINGS.lineOpacity),
        polygonOpacity: readOpacity(s.polygonOpacity, DEFAULT_MVT_LAYER_SETTINGS.polygonOpacity),
        symbolOnlyMode:
          typeof s.symbolOnlyMode === "boolean"
            ? s.symbolOnlyMode
            : DEFAULT_MVT_LAYER_SETTINGS.symbolOnlyMode,
        calloutsEnabled:
          typeof s.calloutsEnabled === "boolean"
            ? s.calloutsEnabled
            : DEFAULT_MVT_LAYER_SETTINGS.calloutsEnabled,
        iconsLabelsMode:
          typeof s.iconsLabelsMode === "boolean"
            ? s.iconsLabelsMode
            : DEFAULT_MVT_LAYER_SETTINGS.iconsLabelsMode,
      };
    }

    return out;
  } catch {
    return {};
  }
}

export function writeMapLayersPersisted(snapshot: MapLayersPersistedSnapshot): void {
  if (typeof window === "undefined") return;
  try {
    const toolToSave = isPersistableBasemap(snapshot.activeTool) ? snapshot.activeTool : null;
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        ...snapshot,
        activeTool: toolToSave,
      }),
    );
  } catch {
    /* private mode / quota */
  }
}

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}

function readOpacity(v: unknown, fallback: number): number {
  if (typeof v !== "number" || !Number.isFinite(v)) return fallback;
  return clamp01(v);
}
